import { useState, useEffect } from "react";
import { toast } from "sonner";
import { customCollectionsService } from "../../services/customCollections";
import { storesService } from "../../services/stores";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Folder, Search, Filter, Check, ChevronsUpDown } from "lucide-react";
import { getImageUrl } from "../../utils/imageUtils";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export default function AdminResellerCollections() {
    const [collections, setCollections] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStore, setFilterStore] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        fetchCollections();
    }, [filterStore, debouncedSearch]);

    const fetchStores = async () => {
        try {
            const data = await storesService.getAll();
            setStores(data);
        } catch (error) {
            console.error("Failed to fetch stores", error);
        }
    };

    const fetchCollections = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterStore !== "all") params.store_id = filterStore;
            if (debouncedSearch) params.search = debouncedSearch;
            params.sort_by = 'store_name';

            // Using the method that hits /api/custom-collections
            const response = await customCollectionsService.getAll(params);
            // service returns { data, pagination } or just data array
            const collectionsData = response && response.data && Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
            setCollections(collectionsData);
        } catch (error) {
            console.error("Failed to fetch reseller collections", error);
            toast.error("Failed to fetch collections");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-reseller-collections">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">
                        Collections
                    </h2>
                    <p className="text-slate-500">View collections from all resellers</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative w-64">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
                        <Input
                            placeholder="Search collections..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8"
                        />
                    </div>
                    <Popover open={storeDropdownOpen} onOpenChange={setStoreDropdownOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={storeDropdownOpen}
                                className="w-64 justify-between"
                            >
                                <div className="flex items-center">
                                    <Filter className="w-4 h-4 mr-2 text-slate-500" />
                                    <span className="truncate">
                                        {filterStore === "all"
                                            ? "All Stores"
                                            : stores.find((s) => s.store_id === filterStore)?.business_name || 
                                              stores.find((s) => s.store_id === filterStore)?.store_name || 
                                              "All Stores"}
                                    </span>
                                </div>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-0" align="end">
                            <Command>
                                <CommandInput placeholder="Search store..." />
                                <CommandList>
                                    <CommandEmpty>No store found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="all"
                                            onSelect={() => {
                                                setFilterStore("all");
                                                setStoreDropdownOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    filterStore === "all" ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            All Stores
                                        </CommandItem>
                                        {stores.map((store) => (
                                            <CommandItem
                                                key={store.store_id}
                                                value={store.business_name || store.store_name}
                                                onSelect={() => {
                                                    setFilterStore(store.store_id);
                                                    setStoreDropdownOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        filterStore === store.store_id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                <span className="truncate">
                                                    {store.business_name || store.store_name || "Unknown Store"}
                                                </span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            <div className="card-elevated overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Collection</TableHead>
                            <TableHead className="font-semibold">Store</TableHead>
                            <TableHead className="font-semibold">Status</TableHead>
                            <TableHead className="font-semibold text-right">Created</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} className="h-32 text-center">
                                    <div className="flex items-center justify-center">
                                        <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : !Array.isArray(collections) || collections.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                                    No collections found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            collections.map((collection) => (
                                <TableRow key={collection._id} className="table-row">
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            {collection.cover_image ? (
                                                <img
                                                    src={getImageUrl(collection.cover_image)}
                                                    alt={collection.name}
                                                    className="w-10 h-10 rounded-lg object-cover"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                                    <Folder className="w-5 h-5 text-slate-400" />
                                                </div>
                                            )}
                                            <div>
                                                <span className="font-medium text-slate-900">{collection.name}</span>
                                                {collection.description && (
                                                    <p className="text-xs text-slate-500 truncate max-w-[200px]">{collection.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="font-medium text-slate-700">
                                            {collection.store_name || "Unknown"}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${collection.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {collection.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right text-slate-500 text-sm">
                                        {new Date(collection.createdAt || collection.created_at).toLocaleDateString()}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
